import { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Filter, Search, X } from 'lucide-react';

/**
 * Reusable Excel-style column filter dropdown.
 *
 * Props:
 *   options      — string[] (full list of distinct values that can be filtered)
 *   selected     — string[] (currently selected values; empty array == "show all")
 *   onChange     — (next: string[]) => void
 *   label        — small label shown in the trigger pill (e.g. "Ryhmä")
 *   testId       — optional data-testid for trigger
 *   placeholder  — search input placeholder
 *   width        — popover width tailwind class (default w-56)
 */
export default function ColumnFilter({
  options,
  selected = [],
  onChange,
  label,
  testId,
  placeholder = 'Hae…',
  width = 'w-56',
  labelMap,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const active = selected.length > 0;
  const optionLabel = (v) => (labelMap && Object.prototype.hasOwnProperty.call(labelMap, v) ? labelMap[v] : v);

  const visible = useMemo(() => {
    const list = (options || []).filter(Boolean);
    const sorted = [...new Set(list)].sort((a, b) => a.localeCompare(b, 'fi', { numeric: true }));
    if (!q.trim()) return sorted;
    const needle = q.toLowerCase();
    return sorted.filter((o) => (optionLabel(o) || '').toLowerCase().includes(needle) || o.toLowerCase().includes(needle));
  }, [options, q, labelMap]);

  const toggle = (val) => {
    const set = new Set(selected);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    onChange(Array.from(set));
  };

  const clear = (e) => {
    if (e) e.stopPropagation();
    onChange([]);
  };

  const selectAll = () => onChange([...visible]);

  const summary = active
    ? selected.length === 1
      ? (optionLabel(selected[0]) || selected[0])
      : `${selected.length} valittu`
    : (label || 'Kaikki');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[11px] font-medium border transition-colors ${
            active
              ? 'bg-[#2A4B30] text-white border-[#2A4B30] hover:bg-[#1E3622]'
              : 'bg-white text-[#6A6D66] border-[#E5E6E3] hover:bg-[#F4F6F3] hover:text-[#1C1C1A]'
          }`}
          title={`Suodata: ${label}`}
        >
          <Filter size={11} />
          <span className="max-w-[10rem] truncate">{summary}</span>
          {active && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clear(e); }}
              className="w-4 h-4 rounded-full hover:bg-white/20 inline-flex items-center justify-center"
              title="Tyhjennä suodatin"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={10} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={`${width} p-0`} align="start">
        <div className="p-2 border-b border-[#E5E6E3] flex items-center gap-2">
          <Search size={12} className="text-[#6A6D66]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="h-7 text-xs border-0 focus-visible:ring-0 px-0"
          />
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-[#6A6D66] border-b border-[#E5E6E3]">
          <button
            type="button"
            onClick={selectAll}
            className="hover:text-[#2A4B30] font-medium"
            data-testid={testId ? `${testId}-select-all` : undefined}
          >
            Valitse kaikki
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            className="hover:text-[#2A4B30] font-medium"
            data-testid={testId ? `${testId}-reset` : undefined}
          >
            Tyhjennä
          </button>
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {visible.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#A0A39C]">Ei tuloksia</div>
          )}
          {visible.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#F4F6F3]"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(opt)}
                  data-testid={testId ? `${testId}-opt-${opt}` : undefined}
                  className="h-3.5 w-3.5"
                />
                <span className="text-[#1C1C1A]">{optionLabel(opt)}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
